/**
 * 260810 E2-P2 — Khối "Phụ đề AI" trong modal sửa tập.
 *
 * Vòng đời: chưa có job → sinh → (đang nghe) → chờ duyệt → admin sửa/duyệt → gắn vào tập.
 * Mọi quyết định (nút nào bật, có phải hỏi lại không) nằm ở `utils/subtitleJobView` để test được —
 * file này chỉ lo phần hiển thị.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Collapse, Popconfirm, Space, Spin, Tag, Typography, message } from 'antd';
import { RobotOutlined, ReloadOutlined, CheckOutlined } from '@ant-design/icons';
import { SubtitleEditorPanel } from './SubtitleEditorPanel';
import { episodesApi } from '../services/api/episodes';
import { SubtitleEntry, SubtitleJob } from '../types';
import {
  deriveJobActions,
  describeJobStatus,
  toEditorEntries,
  fromEditorEntries,
  shouldReloadEntries,
  buildVttFromEntries,
} from '../utils/subtitleJobView';

const { Text } = Typography;

/** Job đang chạy thì hỏi lại mỗi ngần này. Nghe xong một video 2–4 phút mất cỡ 1–4 phút. */
const POLL_MS = 5000;

interface Props {
  episodeId: string;
  videoUrl?: string;
  /** Gọi sau khi duyệt xong để cha nạp lại tập (khối "Saved subtitles" hiện mục mới). */
  onApproved?: () => void;
}

export const EpisodeSubtitleAiPanel: React.FC<Props> = ({ episodeId, videoUrl, onApproved }) => {
  const [job, setJob] = useState<SubtitleJob | null>(null);
  const [entries, setEntries] = useState<SubtitleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLTrackElement>(null);
  /** Chỉ nạp entries từ server khi ĐỔI job — nếu không, mỗi lần hỏi lại sẽ xoá sạch phần admin đang sửa. */
  const loadedJobIdRef = useRef<string | null>(null);

  /**
   * Đường DUY NHẤT để đưa một job vào màn hình.
   *
   * 🔴 Mọi nơi nhận job mới đều phải gọi hàm này. Trước đây `handleRegenerate` tự gán
   * `loadedJobIdRef = job.id` bằng tay — job vừa tạo thì `entries` còn rỗng, khoá vào đó là mọi lượt
   * hỏi sau đều bị bỏ qua, kết quả: dòng meta ghi "12 câu" mà danh sách hiện "0". Đúng lỗi cũ, đường mới.
   */
  const applyJob = useCallback((next: SubtitleJob | null) => {
    setJob(next);
    const { reload, nextLoadedId } = shouldReloadEntries(loadedJobIdRef.current, next);
    if (reload) setEntries(toEditorEntries(next?.entries));
    loadedJobIdRef.current = nextLoadedId;
  }, []);

  const fetchJob = useCallback(async () => {
    try {
      const res = await episodesApi.getLatestSubtitleJob(episodeId);
      applyJob(res.data);
    } catch (e: any) {
      // 404 = tập chưa từng chạy job nào. Đây là trạng thái hợp lệ, KHÔNG phải lỗi.
      if (e?.response?.status === 404) {
        // Tập chưa từng chạy job nào — trạng thái hợp lệ, KHÔNG phải lỗi.
        applyJob(null);
      } else {
        console.error('[EpSub] không lấy được job:', e);
      }
    } finally {
      setLoading(false);
    }
  }, [episodeId, applyJob]);

  useEffect(() => {
    setLoading(true);
    loadedJobIdRef.current = null;
    fetchJob();
  }, [fetchJob]);

  const actions = deriveJobActions(job, entries.length);
  const statusView = describeJobStatus(job);

  // Chỉ hỏi lại khi job thực sự đang chạy; dọn interval khi rời màn hoặc job dừng.
  useEffect(() => {
    if (!actions.shouldPoll) return;
    const timer = setInterval(fetchJob, POLL_MS);
    return () => clearInterval(timer);
  }, [actions.shouldPoll, fetchJob]);

  const handleRegenerate = async () => {
    try {
      setActing(true);
      const res = await episodesApi.regenerateSubtitleJob(episodeId);
      applyJob(res.data);
      message.success('Đang sinh phụ đề bằng AI, việc này chạy nền.');
    } catch (e: any) {
      message.error(e?.response?.data?.messages?.[0] || 'Không sinh được phụ đề');
    } finally {
      setActing(false);
    }
  };

  const handleApprove = async () => {
    if (!job) return;
    try {
      setActing(true);
      await episodesApi.approveSubtitleJob(episodeId, job.id, fromEditorEntries(entries));
      message.success('Đã duyệt và gắn phụ đề vào tập.');
      await fetchJob();
      onApproved?.();
    } catch (e: any) {
      message.error(e?.response?.data?.messages?.[0] || 'Duyệt phụ đề thất bại');
    } finally {
      setActing(false);
    }
  };

  const handleSeek = (time: number) => {
    if (videoRef.current) videoRef.current.currentTime = time;
  };

  // Video chỉ xuất hiện khi job đã có kết quả — dùng làm phụ thuộc cho effect bật phụ đề bên dưới.
  const showEditor = job && (job.status === 'awaiting_review' || job.status === 'approved');

  // Phụ đề gắn vào video được dựng từ bản ĐANG SỬA, không phải file .vtt trên CDN:
  // file đó chỉ có sau khi duyệt, và không phản ánh câu admin vừa gõ.
  const vttUrl = useMemo(() => {
    if (entries.length === 0) return '';
    const blob = new Blob([buildVttFromEntries(entries)], { type: 'text/vtt' });
    return URL.createObjectURL(blob);
  }, [entries]);

  // Blob URL không tự giải phóng — không thu hồi là rò bộ nhớ mỗi lần sửa một câu.
  useEffect(() => {
    return () => {
      if (vttUrl) URL.revokeObjectURL(vttUrl);
    };
  }, [vttUrl]);

  /**
   * Bật phụ đề trên player.
   *
   * 🔴 `default` chỉ ăn khi thẻ <track> có mặt ngay lúc <video> được dựng. Ở đây video chỉ xuất hiện
   * khi job sang `awaiting_review`, tức track được gắn vào một video vừa mới mount — lúc effect chạy
   * thì track chưa tải xong, lệnh bật rơi vào khoảng trống và phụ đề không hiện cho tới khi reload.
   * Vì thế phải bật ở NHIỀU thời điểm: ngay bây giờ, khi track tải xong, khi trình duyệt thêm track
   * vào danh sách, và một lần trễ để phòng các trường hợp còn lại.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !vttUrl) return;

    const enable = () => {
      for (let i = 0; i < video.textTracks.length; i++) {
        video.textTracks[i].mode = 'showing';
      }
    };

    enable();
    const track = trackRef.current;
    track?.addEventListener('load', enable);
    video.textTracks.addEventListener?.('addtrack', enable);
    const timer = setTimeout(enable, 300);

    return () => {
      clearTimeout(timer);
      track?.removeEventListener('load', enable);
      video.textTracks.removeEventListener?.('addtrack', enable);
    };
    // showEditor nằm trong danh sách vì video chỉ tồn tại khi nó bật — đổi giá trị này là video mới mount.
  }, [vttUrl, showEditor]);

  const isHls = !!videoUrl && videoUrl.includes('.m3u8');
  // Chrome/Firefox không phát HLS nếu không có hls.js — repo chưa có, và ta không thêm phụ thuộc
  // chỉ để xem trước. Không xem được video KHÔNG chặn việc sửa/duyệt: mốc thời gian vẫn đọc bằng số.
  const canPlayHls =
    typeof document !== 'undefined' &&
    !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl');
  const videoPlayable = !!videoUrl && (!isHls || canPlayHls);


  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <Space>
          <RobotOutlined />
          <span>Phụ đề AI</span>
          <Tag color={statusView.color} style={{ margin: 0 }}>{statusView.label}</Tag>
        </Space>
      }
      extra={
        <Space>
          {actions.canApprove && (
            <Popconfirm
              title="Duyệt phụ đề này?"
              description="Bản phụ đề sẽ được gắn vào tập, thay cho phụ đề English hiện có (nếu có)."
              onConfirm={handleApprove}
              okText="Duyệt"
              cancelText="Huỷ"
            >
              <Button type="primary" size="small" icon={<CheckOutlined />} loading={acting}>
                Duyệt &amp; gắn vào tập
              </Button>
            </Popconfirm>
          )}
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={acting}
            disabled={!actions.canRegenerate || !videoUrl}
            onClick={handleRegenerate}
          >
            {job ? 'Sinh lại' : 'Sinh phụ đề bằng AI'}
          </Button>
        </Space>
      }
    >
      {!videoUrl && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Tập chưa có video — chưa có gì để nghe.
        </Text>
      )}

      {loading && <Spin size="small" />}

      {!loading && videoUrl && !job && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Tập này chưa chạy sinh phụ đề lần nào. Bấm “Sinh phụ đề bằng AI” để bắt đầu.
        </Text>
      )}

      {actions.isBusy && (
        <Space>
          <Spin size="small" />
          <Text type="secondary" style={{ fontSize: 12 }}>
            AI đang nghe video, tự cập nhật khi xong (mỗi {POLL_MS / 1000} giây kiểm một lần)…
          </Text>
        </Space>
      )}

      {job?.status === 'failed' && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 8 }}
          message="Sinh phụ đề thất bại"
          description={job.errorMessage || 'Không rõ nguyên nhân'}
        />
      )}

      {job?.warning && job.status === 'awaiting_review' && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="Phụ đề có thể chưa đủ"
          description={job.warning}
        />
      )}

      {showEditor && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ width: 240, flexShrink: 0 }}>
              {videoPlayable ? (
                <video
                  ref={videoRef}
                  src={videoUrl}
                  controls
                  // KHÔNG đặt crossOrigin: R2 không trả CORS header nên video sẽ bị chặn, màn hình đen.
                  // Track là blob same-origin, không cần crossOrigin để hiển thị.
                  style={{ width: '100%', borderRadius: 6, background: '#000' }}
                  onTimeUpdate={(e) => setCurrentTime((e.target as HTMLVideoElement).currentTime)}
                  onLoadedMetadata={(e) => {
                    // Một mốc bật phụ đề nữa: lúc này track chắc chắn đã nằm trong danh sách.
                    const tracks = (e.target as HTMLVideoElement).textTracks;
                    for (let i = 0; i < tracks.length; i++) tracks[i].mode = 'showing';
                  }}
                >
                  {vttUrl && (
                    <track
                      ref={trackRef}
                      default
                      kind="subtitles"
                      srcLang={job?.language || 'en'}
                      label="Phụ đề đang sửa"
                      src={vttUrl}
                    />
                  )}
                </video>
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message="Không xem trước được"
                  description={
                    <span style={{ fontSize: 12 }}>
                      Video dạng HLS, trình duyệt này không phát trực tiếp được. Vẫn sửa và duyệt
                      phụ đề bình thường —{' '}
                      <a href={videoUrl} target="_blank" rel="noopener noreferrer">mở video</a>.
                    </span>
                  }
                />
              )}
              <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                {job?.entriesCount ?? entries.length} câu
                {job?.audioDuration ? ` · ${Number(job.audioDuration).toFixed(1)}s` : ''}
                {job?.model ? ` · ${job.model}` : ''}
              </div>
              {job?.vttUrl && (
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  <a href={job.vttUrl} target="_blank" rel="noopener noreferrer">Xem file .vtt đã duyệt</a>
                </div>
              )}
            </div>

            <div style={{ flex: 1, minWidth: 320 }}>
              <Collapse
                size="small"
                items={[{
                  key: 'cues',
                  label: `Danh sách câu (${entries.length})`,
                  children: (
              <SubtitleEditorPanel
                subtitleEntries={entries}
                currentTime={currentTime}
                language={job?.language || 'en'}
                onSeek={handleSeek}
                onUpdateEntry={(index, entry) =>
                  setEntries((prev) => prev.map((e, i) => (i === index ? entry : e)))
                }
                onDeleteEntry={(index) => setEntries((prev) => prev.filter((_, i) => i !== index))}
                onAddEntry={(entry) =>
                  setEntries((prev) => [...prev, entry].sort((a, b) => a.startTime - b.startTime))
                }
              />
                  ),
                }]}
              />
            </div>
          </div>

          {job?.status === 'approved' && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Bản này đã duyệt. Muốn sửa tiếp thì bấm “Sinh lại” để chạy một lượt mới.
            </Text>
          )}
        </>
      )}
    </Card>
  );
};
